import { Component, OnInit, computed, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import axios from 'axios';
import { HelpersService } from 'src/app/services/helpers/helpers.service';
import { environment } from 'src/environments/environment';
import { AdminCaseTableItem } from '../cases.component';
import { WorkflowService } from 'src/app/shared/services/workflow/workflow.service';
import { PayeePopupComponent } from '../../../../shared/payee-popup/payee-popup.component';
import { PayeeAutocompleteComponent } from '../../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { PayeeFormFieldComponent } from '../../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeButtonComponent } from '../../../../shared/payee-buttons/payee-button/payee-button.component';
import { PayeeMiniIconButtonComponent } from '../../../../shared/payee-buttons/payee-mini-icon-button/payee-mini-icon-button.component';
import { PayeeIconComponent } from '../../../../shared/payee-icon/payee-icon.component';
import { NgTemplateOutlet } from '@angular/common';

type ProcessState = {
  id: string;
  label: string;
  isClosed: boolean;
};

type ProcessData = {
  id: string;
  label: string;
  isStart: boolean;
  isDeleted: boolean;
  state: ProcessState;
  states: ProcessState[];
};

type DialogData = {
  processes: Record<string, ProcessData>;
};

@Component({
  selector: 'app-overwrite-state-dialog',
  templateUrl: './overwrite-state-dialog.component.html',
  styleUrls: ['./overwrite-state-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeAutocompleteComponent,
    PayeeFormFieldComponent,
    PayeeButtonComponent,
    PayeeMiniIconButtonComponent,
    PayeeIconComponent,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    NgTemplateOutlet,
  ],
})
export class OverwriteStateDialogComponent implements OnInit {
  private dialogRef =
    inject<MatDialogRef<OverwriteStateDialogComponent>>(MatDialogRef);
  private fb = inject(FormBuilder);
  private helpersService = inject(HelpersService);
  private workflowService = inject(WorkflowService);

  data = inject<AdminCaseTableItem>(MAT_DIALOG_DATA);

  loading = false;
  readonly dialogData = signal<DialogData | undefined>(undefined);

  showIds: Record<string, Set<string>> = {};
  private readonly deletedProcessIds = new Set<string>();
  private readonly restoredProcessIds = new Set<string>();

  readonly form = this.fb.group({
    processes: this.fb.group({}),
  });

  get processes() {
    return this.form.controls.processes;
  }

  readonly activeProcesses = computed(() =>
    Object.values(this.dialogData()?.processes ?? {}).filter(
      (process) => !process.isDeleted && !this.isProcessClosed(process),
    ),
  );

  readonly closedProcesses = computed(() =>
    Object.values(this.dialogData()?.processes ?? {}).filter(
      (process) => !process.isDeleted && this.isProcessClosed(process),
    ),
  );

  readonly deletedProcesses = computed(() =>
    Object.values(this.dialogData()?.processes ?? {}).filter(
      (process) => process.isDeleted,
    ),
  );

  async ngOnInit(): Promise<void> {
    try {
      this.loading = true;

      const result = await axios.get(
        `${environment.baseUrl}/api/${this.data.id}/overwrite-state`,
      );
      const dialogData: DialogData = result.data;
      this.dialogData.set(dialogData);

      for (const process of Object.values(dialogData.processes)) {
        if (!process.isDeleted) {
          this.addProcessControl(process);
        }
      }

      this.loading = false;
    } catch (error) {
      console.error('Error while loading dialog data', error);
      this.cancel();
    }
  }

  async submit(): Promise<void> {
    if (this.form.invalid || this.loading) {
      this.helpersService.markAllChildrenAsDirty(this.form);
      return;
    }

    const { processes } = this.form.getRawValue();

    try {
      this.loading = true;

      const result = await axios.post(
        `${environment.baseUrl}/api/${this.data.id}/overwrite-state`,
        {
          processes: Object.entries(processes).map(([processId, state]) => ({
            id: processId,
            state: (state as ProcessState).id,
          })),
          deletedProcesses: [...this.deletedProcessIds],
          restoredProcesses: [...this.restoredProcessIds],
        },
      );
      this.workflowService.afterWorkflow.next(result.data);
      this.dialogRef.close(true);
    } catch (error) {
      console.error('Error while overwriting states', error);
    } finally {
      this.loading = false;
    }
  }

  deleteSubprocess(processId: string): void {
    const dialogData = this.dialogData();
    const process = dialogData?.processes[processId];

    if (!dialogData || !process || process.isStart || this.loading) {
      return;
    }

    if (!this.restoredProcessIds.delete(processId)) {
      this.deletedProcessIds.add(processId);
    }
    this.processes.removeControl(processId);
    this.dialogData.update((currentDialogData) => {
      if (!currentDialogData) {
        return currentDialogData;
      }

      return {
        ...currentDialogData,
        processes: {
          ...currentDialogData.processes,
          [processId]: {
            ...process,
            isDeleted: true,
          },
        },
      };
    });
    delete this.showIds[processId];
  }

  restoreSubprocess(processId: string): void {
    const dialogData = this.dialogData();
    const process = dialogData?.processes[processId];

    if (!dialogData || !process?.isDeleted || this.loading) {
      return;
    }

    if (!this.deletedProcessIds.delete(processId)) {
      this.restoredProcessIds.add(processId);
    }
    this.addProcessControl(process);
    this.dialogData.update((currentDialogData) => {
      if (!currentDialogData) {
        return currentDialogData;
      }

      return {
        ...currentDialogData,
        processes: {
          ...currentDialogData.processes,
          [processId]: {
            ...process,
            isDeleted: false,
          },
        },
      };
    });
  }

  getProcessControl(processId: string): FormControl<ProcessState | null> {
    return this.processes.get(processId) as FormControl<ProcessState | null>;
  }

  readonly stateDisplayWith = (state?: ProcessState): string => {
    return state?.label ?? '';
  };

  readonly cancel = (): void => {
    this.dialogRef.close();
  };

  private addProcessControl(process: ProcessData): void {
    this.processes.setControl(
      process.id,
      this.fb.control<ProcessState | null>(
        process.state,
        Validators.required,
      ),
    );

    const stateLabelCounts = new Map<string, number>();
    for (const state of process.states) {
      stateLabelCounts.set(
        state.label,
        (stateLabelCounts.get(state.label) ?? 0) + 1,
      );
    }

    for (const [label, count] of stateLabelCounts) {
      if (count > 1) {
        this.showIds[process.id] ??= new Set<string>();
        this.showIds[process.id].add(label);
      }
    }
  }

  private isProcessClosed(process: ProcessData): boolean {
    return process.state.isClosed && !process.isStart;
  }
}
